// GalleryModal.js
import React, { useState, useEffect, useMemo } from 'react';
import './GalleryModal.css';

const GalleryModal = ({
  isOpen,
  onClose,
  images = [],
  startIndex = 0,
  title = '',
}) => {
  const [index, setIndex] = useState(startIndex);
  const [touchStartX, setTouchStartX] = useState(null);

  // keep index in-sync with startIndex when opening
  useEffect(() => {
    if (isOpen) setIndex(startIndex);
  }, [isOpen, startIndex]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const hasImages = images && images.length > 0;
  const current = hasImages ? images[(index + images.length) % images.length] : null;

  const goPrev = () => setIndex((i) => (i - 1 + images.length) % images.length);
  const goNext = () => setIndex((i) => (i + 1) % images.length);

  // keyboard support
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // basic swipe
  const onTouchStart = (e) => {
    if (e.touches && e.touches.length === 1) {
      setTouchStartX(e.touches[0].clientX);
    }
  };
  const onTouchEnd = (e) => {
    if (touchStartX == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX;
    const SWIPE_THRESHOLD = 50; // px
    if (dx > SWIPE_THRESHOLD) goPrev();
    else if (dx < -SWIPE_THRESHOLD) goNext();
    setTouchStartX(null);
  };

  if (!isOpen) return null;

  return (
    <div
      className="gallery-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 2000,
      }}
    >
      {/* container to prevent image click from closing */}
      <div
        className="gallery-container"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        style={{
          position: 'relative', width: '95%', height: '90%',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
        }}
      >
        {/* header */}
        <div style={{
          width: '100%', display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', color: 'white', marginBottom: 8
        }}>
          <div style={{ fontSize: 14, opacity: 0.9 }}>
            {title} {hasImages ? `(${index + 1}/${images.length})` : ''}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.5)',
              borderRadius: 8, padding: '4px 10px', cursor: 'pointer'
            }}
          >
            關閉
          </button>
        </div>

        {/* image area */}
        <div style={{
          flex: 1, width: '100%', display: 'flex',
          alignItems: 'center', justifyContent: 'center', position: 'relative'
        }}>
          {/* Prev */}
          {images.length > 1 && (
            <button
              onClick={goPrev}
              aria-label="Previous"
              style={{
                position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(0,0,0,0.4)', border: 'none', color: 'white',
                padding: '8px 10px', borderRadius: 8, cursor: 'pointer'
              }}
            >
              ‹
            </button>
          )}

          {/* Current image */}
          {current ? (
            <img
              src={current}
              alt="gallery"
              style={{
                maxWidth: '100%', maxHeight: '100%', objectFit: 'contain',
                borderRadius: 8, boxShadow: '0 10px 30px rgba(0,0,0,0.4)'
              }}
            />
          ) : (
            <div style={{ color: 'white' }}>No image</div>
          )}

          {/* Next */}
          {images.length > 1 && (
            <button
              onClick={goNext}
              aria-label="Next"
              style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(0,0,0,0.4)', border: 'none', color: 'white',
                padding: '8px 10px', borderRadius: 8, cursor: 'pointer'
              }}
            >
              ›
            </button>
          )}
        </div>

        {/* thumbnails row (optional) */}
        {images.length > 1 && (
          <div
            className="gallery-thumbs"
            style={{ marginTop: 10, width: '100%', overflowX: 'auto', whiteSpace: 'nowrap' }}
          >
            {images.map((src, i) => (
              <img
                key={i}
                src={src}
                onClick={() => setIndex(i)}
                alt={`thumb-${i}`}
                style={{
                  height: 56, width: 56, objectFit: 'cover',
                  borderRadius: 6, marginRight: 8,
                  border: i === index ? '2px solid #4e99e1' : '2px solid transparent',
                  cursor: 'pointer'
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GalleryModal;


